"use client";

import * as React from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertTriangle } from "lucide-react";
import { cn } from "../../../lib/utils";
import { kanbanColumnVariants } from "./constants";
import {
  KanbanColumnProvider,
  KanbanColumnDragHandleProvider,
  useKanbanBoard,
} from "./context";
import { KanbanColumnHeader as KanbanColumnHeaderComponent } from "./kanban-column-header";
import { KanbanCard as KanbanCardComponent } from "./kanban-card";
import type { KanbanColumnProps } from "./types";

const KanbanColumn = React.forwardRef<HTMLDivElement, KanbanColumnProps>(
  ({ className, column, children, ...props }, ref) => {
    const { activeDrag } = useKanbanBoard();

    const {
      attributes,
      listeners,
      setNodeRef: setSortableRef,
      transform,
      transition,
      isDragging: isSortableDragging,
    } = useSortable({
      id: column.id,
      data: { type: "column", column },
    });

    const dropZoneId = `${column.id}:drop-zone`;
    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
      id: dropZoneId,
      data: { type: "column", column },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const cardIds = React.useMemo(
      () => column.cards.map((c) => c.id),
      [column.cards],
    );

    const isDragging =
      isSortableDragging ||
      (activeDrag?.type === "column" && activeDrag.id === column.id);
    const state = isDragging ? "dragging" : isOver ? "dragOver" : "default";

    const columnCtx = React.useMemo(
      () => ({ column, isOver, isDragging }),
      [column, isOver, isDragging],
    );

    const dragHandleCtx = React.useMemo(
      () => ({
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: listeners as unknown as Record<string, unknown> | undefined,
      }),
      [attributes, listeners],
    );

    const { header, footer, cardElements, otherChildren } =
      React.useMemo(() => {
        const result = {
          header: null as React.ReactNode,
          footer: null as React.ReactNode,
          cardElements: [] as React.ReactNode[],
          otherChildren: [] as React.ReactNode[],
        };

        React.Children.forEach(children, (child) => {
          if (!React.isValidElement(child)) {
            result.otherChildren.push(child);
            return;
          }

          if (child.type === KanbanColumnHeaderComponent) {
            result.header = child;
          } else if (
            (child.type as { displayName?: string })?.displayName ===
            "KanbanColumnFooter"
          ) {
            result.footer = child;
          } else if (child.type === KanbanCardComponent) {
            result.cardElements.push(child);
          } else {
            result.otherChildren.push(child);
          }
        });

        return result;
      }, [children]);

    return (
      <KanbanColumnProvider value={columnCtx}>
        <KanbanColumnDragHandleProvider value={dragHandleCtx}>
          <div
            ref={(node) => {
              setSortableRef(node);
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            style={style}
            className={cn(kanbanColumnVariants({ state }), className)}
            {...props}
          >
            {header}
            {column.wipLimit != null &&
              column.wipLimit > 0 &&
              column.cards.length >= column.wipLimit && (
                <div className="mb-1 flex items-center gap-1.5 rounded-sm bg-warning-subtle px-2 py-1 text-xs font-medium text-warning">
                  <AlertTriangle className="size-3 shrink-0" aria-hidden="true" />
                  WIP limit reached
                </div>
              )}
            <SortableContext
              items={cardIds}
              strategy={verticalListSortingStrategy}
            >
              <div
                ref={setDroppableRef}
                /* `relative` is load-bearing, not decoration. Cards carry
                   `sr-only` labels, and Tailwind's `sr-only` is
                   `position: absolute` — with no positioned ancestor inside the
                   board their containing block was the page column, which sits
                   OUTSIDE this scroller. An abspos box is only clipped by
                   overflow ancestors between it and its containing block, so
                   every off-screen card's label leaked its full static offset
                   (thousands of px) as layout overflow onto the page, giving
                   the app frame a phantom scroll into blank space. Making the
                   scroller the containing block keeps that overflow local. */
                className="relative flex-1 overflow-y-auto space-y-2.5 min-h-25"
              >
                {cardElements}
                {otherChildren}
              </div>
            </SortableContext>
            {footer}
          </div>
        </KanbanColumnDragHandleProvider>
      </KanbanColumnProvider>
    );
  },
);

KanbanColumn.displayName = "KanbanColumn";

export { KanbanColumn };
